/* @flow */
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import Parallax from 'parallax-js';
import classNames from 'classnames';

import imageLogo from '../../assets/images/Protones - Logo Rosso Scuro.png';
import imageWord from '../../assets/images/Protones - Scritta Rosso Scuro.png';
import videoLogo from '../../assets/videos/MEDIALOGO PROTONES DEF.mp4';
import Button from '../../components/Button';
import Video from '../../components/Video';
import keys from '../../config/keys';
import LoginForm from '../../forms/LoginForm';
import actions from '../../redux/actions';
import { useClickOutside } from '../../utils/hooks';

import type { ReduxState, Translate } from '../../types';

import styles from './styles.module.scss';

type FormProps = {
  onClose?: () => any,
  username?: string,
  password?: string,
};

const Form = ({ onClose, username, password }: FormProps) => {
  const ref = useRef();
  useClickOutside(ref, onClose || (() => {}));
  return (
    <div ref={ref}>
      <LoginForm forgotUrl={`${keys.LIFERAY_FORGOT_URL}`} username={username} password={password} />
    </div>
  );
};

const videoStartTime = 2.7;
const videoEndTime = 63;
const transitionMilliseconds = 300;
const parallaxEffects = ['0.09', '0.09', '0.18', '0.5'];
const [parallaxStars, parallaxTwinkling, parallaxLogoImage, parallaxClouds] = parallaxEffects;
const initialCredentials = {
  username: '',
  password: '',
};
const demoCredentials = {
  username: 'demo@protones.it',
  password: 'demo',
};

type Props = {
  login: (username: string, password: string) => mixed,
  t: Translate,
};

const mapStateToProps = (state: ReduxState) => ({});

const Login = ({ login, t }: Props) => {
  const parallaxRef = useRef();
  const videoRef = useRef();
  useEffect(() => {
    const parallaxContainer = parallaxRef.current;
    if (parallaxContainer) {
      new Parallax(parallaxContainer);
    }
  }, [parallaxRef]);
  const [isLoginFormVisible, setLoginFormVisible] = useState(false);
  const [isVideoPlaying, setVideoPlaying] = useState(false);
  const [credentials, setCredentials] = useState(initialCredentials);
  const handleDemoClick = useCallback(() => {
    if (isVideoPlaying) {
      return;
    }
    setLoginFormVisible(true);
    setCredentials(() => initialCredentials);
    setCredentials(() => demoCredentials);
    // login(demoCredentials.username, demoCredentials.password);
  }, [isVideoPlaying, setLoginFormVisible, setCredentials]);
  const handleLoginClick = useCallback(() => !isVideoPlaying && setLoginFormVisible(true), [
    isVideoPlaying,
    setLoginFormVisible,
  ]);
  const handleTogglePlayClick = useCallback(
    () =>
      setVideoPlaying(isPlaying => {
        const video = videoRef.current;
        if (video) {
          setTimeout(() => {
            isPlaying ? video.pause() : video.play();
            video.currentTime = videoStartTime;
          }, transitionMilliseconds);
          if (!isPlaying) {
            setTimeout(() => {
              setVideoPlaying(false);
            }, (videoEndTime - videoStartTime) * 1000 + transitionMilliseconds);
          }
        }
        return !isPlaying;
      }),
    [videoRef, setVideoPlaying]
  );

  return (
    <div className={styles.container}>
      <div
        className={styles.sky}
        ref={parallaxRef}
        style={{
          opacity: isVideoPlaying ? 0 : 1,
          transitionDelay: isVideoPlaying ? '0s' : `${transitionMilliseconds}ms`,
        }}
      >
        <div data-depth={parallaxStars} className={styles.stars} />
        <div data-depth={parallaxTwinkling} className={styles.twinkling} />
        <div
          data-depth={parallaxLogoImage}
          className={styles.logoImage}
          onClick={handleTogglePlayClick}
        >
          <img
            src={imageLogo}
            alt={'logo'}
            style={{
              opacity: isLoginFormVisible ? 0 : 1,
              transform: `scale(${isLoginFormVisible ? 0 : 1})`,
              transition: 'transform 0.3s ease-in-out',
              width: '100%',
            }}
          />
        </div>
        <div data-depth={parallaxClouds} className={styles.clouds} />
      </div>
      <div className={styles.foreground}>
        <div
          className={styles.header}
          style={{
            opacity: isVideoPlaying ? 0 : 1,
            transform: `scale(${isVideoPlaying ? 0.95 : 1})`,
            transitionDelay: isVideoPlaying ? '0s' : `${transitionMilliseconds}ms`,
            pointerEvents: isVideoPlaying ? 'none' : 'all',
          }}
        >
          <span className={styles.glassReflection}>
            <Button text={t('DEMO')} onClick={handleDemoClick} appearance={'jarvis'} />
          </span>
          <span className={styles.glassReflection}>
            <Button text={t('LOGIN')} onClick={handleLoginClick} appearance={'jarvis'} />
          </span>
        </div>
        <div className={styles.content}>
          <div
            className={styles.form}
            style={{
              opacity: isLoginFormVisible ? 1 : 0,
              transform: `translate(0, -50%) scale(${isLoginFormVisible ? 1 : 0})`,
            }}
          >
            <Form
              onClose={() => isLoginFormVisible && setLoginFormVisible(false)}
              username={credentials.username}
              password={credentials.password}
            />
          </div>
          <div
            className={styles.media}
            style={{
              opacity: isLoginFormVisible ? 0 : 1,
              transform: `scale(${isLoginFormVisible ? 0 : 1})`,
            }}
          >
            <img
              className={classNames(styles.wordImage, styles.glassReflection)}
              src={imageWord}
              alt={'logo'}
              style={{
                opacity: isVideoPlaying ? 0 : 1,
              }}
            />
            <Video
              ref={videoRef}
              className={styles.logoVideo}
              src={videoLogo}
              controls={true}
              style={{
                opacity: isVideoPlaying ? 1 : 0,
                transitionDelay: isVideoPlaying ? `${transitionMilliseconds}ms` : '0s',
                pointerEvents: isVideoPlaying ? 'all' : 'none',
              }}
              muted={!isVideoPlaying}
              volume={1}
              onClick={handleTogglePlayClick}
            />
          </div>
        </div>
        <div
          className={styles.footer}
          style={{
            opacity: isVideoPlaying ? 0 : 1,
            transform: `scale(${isVideoPlaying ? 0.95 : 1})`,
            transitionDelay: isVideoPlaying ? '0s' : `${transitionMilliseconds}ms`,
            pointerEvents: isVideoPlaying ? 'none' : 'all',
          }}
        >
          <p className={styles.glassReflection}>
            {`© 2019 Copyright `}
            <a
              style={{ color: 'inherit', textDecoration: 'underline' }}
              href={'http://www.lacosa.it/'}
              rel={'external'}
            >{`LA COSA Srl`}</a>
            {` - info@lacosa.it - ${t(
              'LEGAL_OFFICE'
            )}: via degli Imbriani, 20 – 20158 Milano – R.E.A Mi 1703945 – P.I. / C.F 03821350968 – ${t(
              'REGISTERED_CAPITAL'
            )}: € 10.000. All Rights Reserved. `}
            <a
              style={{ color: 'inherit', textDecoration: 'underline' }}
              href={'http://www.lacosa.it/privacy/'}
              rel={'external'}
            >{`Privacy`}</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default withTranslation()(connect(mapStateToProps, actions)(Login));
